import React, { useState, useEffect } from 'react'
import Image from 'next/image'
import { ArticlesApi } from '@services/api/articles'
import NewscastsService, { Newscast } from '@services/api/newscasts'
import { Article } from 'types/article'

type TabType = 'latest' | 'most-read' | 'guide'

interface ArticlesTabsProps {
  className?: string
  articlesPerPage?: number
  showGuideTab?: boolean
  initialLastArticles?: Article[]
  initialMostReadArticles?: Article[]
  initialNewscasts?: Newscast[]
}

export const ArticlesTabs: React.FC<ArticlesTabsProps> = ({
  className = '',
  articlesPerPage = 9,
  showGuideTab = true,
  initialLastArticles,
  initialMostReadArticles,
  initialNewscasts
}) => {
  const [selectedTab, setSelectedTab] = useState<TabType>('guide')
  const [lastArticles, setLastArticles] = useState<Article[]>(
    initialLastArticles || []
  )
  const [mostReadArticles, setMostReadArticles] = useState<Article[]>(
    initialMostReadArticles || []
  )
  const [newscasts, setNewscasts] = useState<Newscast[]>(initialNewscasts || [])
  const [loading, setLoading] = useState(
    !(initialLastArticles && initialMostReadArticles)
  )

  useEffect(() => {
    // Se já temos dados iniciais, não buscar novamente
    if (
      initialLastArticles &&
      initialMostReadArticles &&
      (!showGuideTab || initialNewscasts)
    ) {
      return
    }

    const fetchData = async () => {
      try {
        setLoading(true)

        console.log('ArticlesTabs: Fetching data...')
        const [lastArticlesRes, mostReadRes, newscastsRes] = await Promise.all([
          ArticlesApi.index({ per: articlesPerPage }),
          ArticlesApi.index({ per: articlesPerPage, more_reads: true }),
          showGuideTab
            ? NewscastsService.index({ size: articlesPerPage })
            : Promise.resolve({ data: [] })
        ])

        console.log('ArticlesTabs: Data fetched successfully')
        setLastArticles(lastArticlesRes.data.articles)
        setMostReadArticles(mostReadRes.data.articles)
        if (showGuideTab) {
          setNewscasts(newscastsRes.data)
        }
      } catch (error) {
        console.error('ArticlesTabs: Error fetching data:', error)
        setLastArticles([])
        setMostReadArticles([])
        setNewscasts([])
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [
    articlesPerPage,
    showGuideTab,
    initialLastArticles,
    initialMostReadArticles,
    initialNewscasts
  ])

  const displayArticles =
    selectedTab === 'most-read' ? mostReadArticles : lastArticles

  return (
    <section className={`container px-4 py-8 mx-auto ${className}`}>
      <div className="flex space-x-4 mb-8">
        {/* <button
          onClick={() => setSelectedTab('latest')}
          className={`text-sm pb-1.5 pt-2 text-[11px] leading-4 tracking-[1.2px] rounded-full px-4 md:px-6 transition-all duration-300 ${
            selectedTab === 'latest'
              ? 'border-transparent bg-gradient-to-r from-[#ffc25f] to-[#ff9c00] hover:brightness-105'
              : 'bg-white shadow-[inset_0_0_0_1px_black] hover:bg-gradient-to-r hover:from-[#ffc25f] hover:to-[#ff9c00] text-gray-700 p-4 transition'
          }`}
        >
          ÚLTIMAS NOTÍCIAS
        </button> */}
        <button
          onClick={() => setSelectedTab('most-read')}
          className={`text-sm pb-1.5 pt-2 text-[11px] leading-4 tracking-[1.2px] rounded-full px-4 md:px-6 transition-all duration-300 ${
            selectedTab === 'most-read'
              ? 'border-transparent bg-gradient-to-r from-[#ffc25f] to-[#ff9c00] hover:brightness-105'
              : 'bg-white shadow-[inset_0_0_0_1px_black] hover:bg-gradient-to-r hover:from-[#ffc25f] hover:to-[#ff9c00] text-gray-700 p-4 transition'
          }`}
        >
          MAIS LIDAS
        </button>
        {showGuideTab && (
          <button
            onClick={() => setSelectedTab('guide')}
            className={`text-sm pb-1.5 pt-2 text-[11px] leading-4 tracking-[1.2px] rounded-full px-4 md:px-6 transition-all duration-300 ${
              selectedTab === 'guide'
                ? 'border-transparent bg-gradient-to-r from-[#ffc25f] to-[#ff9c00] hover:brightness-105'
                : 'bg-white shadow-[inset_0_0_0_1px_black] hover:bg-gradient-to-r hover:from-[#ffc25f] hover:to-[#ff9c00] text-gray-700 p-4 transition'
            }`}
          >
            GUIA SOBRE ENERGIA SOLAR
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">Carregando...</p>
        </div>
      ) : (
        <>
          {selectedTab === 'guide' ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {newscasts.slice(0, articlesPerPage).map(newscast => (
                <a
                  key={newscast.id}
                  href={newscast.url}
                  className="cursor-pointer font-noto w-full h-full flex flex-col"
                >
                  <div className="bg-white rounded-lg shadow-md overflow-hidden h-96">
                    <div className="relative h-60">
                      <Image
                        src={
                          newscast.image?.path || '/assets/default-newscast.jpg'
                        }
                        alt={newscast.image?.alt || newscast.title}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="p-4 relative h-36">
                      <h3 className="font-bold text-lg mb-1 h-14 overflow-hidden">
                        {newscast.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2 line-clamp-3">
                        {newscast.description || 'Portal Solar'}
                      </p>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <>
              {displayArticles && displayArticles.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {displayArticles.slice(0, articlesPerPage).map(article => (
                    <a
                      key={article.id}
                      href={article.path}
                      className="cursor-pointer font-noto w-full h-full flex flex-col"
                    >
                      <div className="bg-white rounded-lg shadow-md overflow-hidden h-96">
                        <div className="relative h-56">
                          <Image
                            src={
                              article.thumbnailImage?.path ||
                              '/assets/default-article.jpg'
                            }
                            alt={article.thumbnailImage?.alt || article.title}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <div className="p-4 relative h-40">
                          <span className="bg-green text-white px-3 py-1 rounded text-sm font-medium mr-2 mb-2">
                            {article.editorial?.name || 'NOTÍCIA'}
                          </span>
                          <h3 className="font-bold text-lg mb-0 h-14 overflow-hidden line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                            {article.shortContent ||
                              'Conteúdo da notícia em destaque.'}
                          </p>
                          <p className="text-xs text-gray-500 flex justify-between w-full absolute bottom-1">
                            <span>
                              {new Date(article.publishedAt).toLocaleDateString(
                                'pt-BR'
                              )}
                            </span>
                            <span className="pe-8">
                              {article.timeToRead} min de leitura
                            </span>
                          </p>
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">
                    {selectedTab === 'most-read'
                      ? 'Carregando artigos mais lidos...'
                      : 'Carregando notícias...'}
                  </p>
                  <p className="text-gray-400 text-sm mt-2">
                    (Aguardando dados da API)
                  </p>
                </div>
              )}
            </>
          )}
        </>
      )}
    </section>
  )
}
